import { GoogleLoginProvider, SOCIAL_AUTH_CONFIG, SocialAuthServiceConfig } from '@abacritt/angularx-social-login';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, inject, provideAppInitializer } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import { Adjust } from '@awesome-cordova-plugins/adjust/ngx';
import { File } from '@awesome-cordova-plugins/file/ngx';
import { ScreenOrientation } from '@awesome-cordova-plugins/screen-orientation/ngx';
import { AppMissingTranslationHandler } from '@core/locale/missing-translation.handler';
import { TranslationHttpLoaderFactory } from '@core/locale/translation-http-loader';
import { PreferenceService } from '@dc-core/dc-services/preference/preference.service';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import {
    MissingTranslationHandler,
    provideTranslateService,
    TranslateLoader,
    TranslateService,
} from '@ngx-translate/core';
import { RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha-2';
import { firstValueFrom, take } from 'rxjs';
import { LocalStorageKey } from 'src/app/constants/local-storage.keys';
import { provideApi } from 'src/app/types/venue/openapi';
import { environment, socials } from 'src/environments/environment';

import { routes } from './app.routes';

function initializeTranslations(): Promise<unknown> {
    const preferenceService = inject(PreferenceService);
    const translateService = inject(TranslateService);
    const languageFromStorage = localStorage.getItem(LocalStorageKey.language);
    const effectiveLanguage =
        preferenceService.sessionLangSignal() ?? languageFromStorage ?? preferenceService.langSignal();

    if (languageFromStorage && languageFromStorage !== preferenceService.langSignal()) {
        preferenceService.langSignal.set(languageFromStorage);
        preferenceService.savePreferences();
    }

    return firstValueFrom(translateService.use(effectiveLanguage).pipe(take(1)));
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(routes),
        provideHttpClient(withInterceptorsFromDi()),
        provideAnimations(),
        provideApi({ basePath: environment.venueApiUrl }),
        provideTranslateService({
            defaultLanguage: 'en',
            loader: {
                provide: TranslateLoader,
                useFactory: TranslationHttpLoaderFactory,
                deps: [HttpClient],
            },
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: AppMissingTranslationHandler,
            },
        }),
        provideAppInitializer(initializeTranslations),

        provideIonicAngular({
            useSetInputAPI: true,
        }),
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },

        // {
        //     provide: ErrorHandler,
        //     useClass: ErrorHandlerService,
        // },

        {
            provide: SOCIAL_AUTH_CONFIG,
            useValue: {
                autoLogin: false,
                lang: 'en',
                providers: environment.isWeb
                    ? []
                    : [
                          {
                              id: GoogleLoginProvider.PROVIDER_ID,
                              provider: new GoogleLoginProvider(socials.google.clientId, {
                                  scopes: ['profile', 'email'],
                              }),
                          },
                      ],
                onError: (err) => {
                    console.error(err);
                },
            } as SocialAuthServiceConfig,
        },
        { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptcha },

        Adjust,
        ScreenOrientation,
        File,
    ],
};
